import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Bell } from 'lucide-react';
import { useSendPersonalNotification } from '../hooks/api';
import FormDrawer, { FormSection } from './FormDrawer';
import { Button } from './ui';

const emptyForm = {
  subject: '',
  message: '',
  priority: 'medium',
  expires_at: '',
};

/**
 * Admin drawer to send a personal notification to a specific user.
 */
const SendNotificationModal = ({ open, onClose, targetUserId, targetUserName }) => {
  const [form, setForm] = useState(emptyForm);
  const sendMutation = useSendPersonalNotification();

  useEffect(() => {
    if (open) setForm(emptyForm);
  }, [open, targetUserId]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    try {
      await sendMutation.mutateAsync({
        target_user_id: targetUserId,
        subject: form.subject.trim(),
        message: form.message.trim(),
        priority: form.priority,
        expires_at: form.expires_at
          ? new Date(`${form.expires_at}T23:59:59`).toISOString()
          : null,
      });
      toast.success('Notification sent');
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send notification');
    }
  };

  return (
    <FormDrawer
      open={open}
      onClose={onClose}
      title="Send Notification"
      subtitle={targetUserName ? `To ${targetUserName}` : 'Personal message'}
      icon={<Bell className="h-5 w-5" />}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={sendMutation.isPending}
            data-testid="send-notification-submit"
          >
            Send
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="send-notification-form">
        <FormSection title="Message">
          <label className="block text-sm font-medium mb-1">Subject</label>
          <input
            className="input-field"
            value={form.subject}
            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
            data-testid="notification-subject"
            required
          />
          <label className="block text-sm font-medium mb-1 mt-3">Message</label>
          <textarea
            className="input-field min-h-[120px]"
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            data-testid="notification-message"
            required
          />
        </FormSection>
        <FormSection title="Options">
          <label className="block text-sm font-medium mb-1">Priority</label>
          <select
            className="input-field"
            value={form.priority}
            onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
            data-testid="notification-priority"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
          <label className="block text-sm font-medium mb-1 mt-3">Due date (optional)</label>
          <input
            type="date"
            className="input-field"
            value={form.expires_at}
            onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
          />
        </FormSection>
      </form>
    </FormDrawer>
  );
};

export default SendNotificationModal;
